


import React from "react";
import Button from "../../components/ui/Button";
import Spinner from "../../components/ui/Spinner";
import api from "../../api/api";

// --- helpers ---------------------------------------------------------------
const normalizeRow = (row) => {
  const rate = Number(row.Rate ?? row.SaleRate ?? row.rate ?? 0);
  const qty = Number(row.Qty ?? row.qty ?? 0);
  const amount = Number(row.Amount ?? row.amount ?? 0); // API’s net amount
  const gross = rate * qty;

  const net = amount > 0 ? amount : gross;

  return {
    ...row,
    __Gross: gross,
    __Net: Math.round(net * 100) / 100,
  };
};

// --- component -------------------------------------------------------------
const StockItems = ({
  orderId,
  stockId,
  orderDate,
  items = [],
  user,
  setError,
  refetchData,
}) => {
  const [itemMaster, setItemMaster] = React.useState([]); // dropdown items
  const [availableItems, setAvailableItems] = React.useState(items);
  const [selectedItem, setSelectedItem] = React.useState(null);

  const [rate, setRate] = React.useState("");
  const [quantity, setQuantity] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const [itemSearch, setItemSearch] = React.useState("");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [filteredItems, setFilteredItems] = React.useState([]);

  const [cpid, setCpid] = React.useState(0);
  const [vrid, setVrid] = React.useState(0);

  // 🔹 Load stock order items + item master
  React.useEffect(() => {
    if (!orderId) return;

    api
      .getselectedStock(orderId)
      .then((data) => {
        const normalized = (data.Table2 || []).map(normalizeRow);
        setAvailableItems(normalized);

        setItemMaster(data.Table1 || []);

        if (data.Table && data.Table.length > 0) {
          setCpid(data.Table[0].cpid || 0);
          setVrid(data.Table[0].Vrid || 0);
        }
      })
      .catch(() => setError("Failed to load stock items."));
  }, [orderId, setError]);

  // 🔹 Filter dropdown
  React.useEffect(() => {
    if (itemSearch) {
      const results = itemMaster.filter(
        (i) =>
          i.ItemNo?.toLowerCase().includes(itemSearch.toLowerCase()) ||
          i.ItemCode?.toLowerCase().includes(itemSearch.toLowerCase()) ||
          i.ItemName?.toLowerCase().includes(itemSearch.toLowerCase()) ||
          i.barcode?.toLowerCase().includes(itemSearch.toLowerCase())
      );
      setFilteredItems(results);
    } else {
      setFilteredItems([]);
    }
  }, [itemSearch, itemMaster]);

  const normalizedItems = React.useMemo(() => {
    return (availableItems || []).map(normalizeRow);
  }, [availableItems]);

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setItemSearch(item.ItemNo || item.ItemCode || item.ItemName || "");
    setRate(item.Rate ?? item.SaleRate ?? "");
    setIsDropdownOpen(false);
  };

  const handleAddItem = async () => {
    if (!selectedItem || !quantity || !rate) {
      setError("Please select an item and enter quantity & rate.");
      return;
    }
    setLoading(true);
    try {
      const qty = parseFloat(quantity);
      const rt = parseFloat(rate);
      const amount = qty * rt;

      let orderDateISO = new Date(orderDate);
      if (isNaN(orderDateISO.getTime())) {
        orderDateISO = new Date();
      }

      const payload = {
        User: user?.UsrName || "admin",
        Vid: stockId,
        OrderDate: orderDateISO.toISOString(),
        ThisPoId: orderId,
        Itemid: selectedItem.ItemId,
        Qty: qty,
        Rate: rt,
        DiscPer: 0,
        Amount: amount,
        SessionId: "9944010155",
        Notes: "",
      };

      console.log("🔍 SaveStock payload:", payload);

      await api.saveStock(payload);

      await refetchData();

      setSelectedItem(null);
      setQuantity("");
      setRate("");
      setItemSearch("");
    } catch (err) {
      console.error("❌ SaveStock error:", err);
      setError("Failed to add item.");
    } finally {
      setLoading(false);
    }
  };


  // --- Delete item function
  const handleDeleteItem = async (item) => {
    if (!orderId || !item?.seqid) {
      setError("Invalid item for deletion.");
      return;
    }
    setLoading(true);
    try {
      await api.mobdeleteStockitem(orderId, item.seqid);
      await refetchData();
    } catch (err) {
      console.error("❌ DeleteStockItem error:", err);
      setError("Failed to delete item.");
    } finally {
      setLoading(false);
    }
  };


  const totalAmount = normalizedItems.reduce(
    (sum, i) => sum + (Number(i.__Net) || 0),
    0
  );

  return (
    <div className="w-full m-0 p-0">
      {/* Add Item Section */}
      <div className="border-b pb-4">
        <h3 className="mb-2 text-lg font-semibold">Add New Item</h3>
        <div className="grid items-end grid-cols-1 gap-2 sm:grid-cols-4">
          {/* Search box */}
          <div className="relative sm:col-span-2">
            <label
              htmlFor="item-search"
              className="block text-sm font-medium text-gray-700"
            >
              Item Name*
            </label>
            <input
              id="item-search"
              type="text"
              value={itemSearch}
              onChange={(e) => {
                setSelectedItem(null);
                setItemSearch(e.target.value);
              }}
              onFocus={() => {
                setIsDropdownOpen(true);
                if (!itemSearch) setFilteredItems(itemMaster);
              }}
              className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
              placeholder="Search item by name/code"
              autoComplete="off"
            />
            {isDropdownOpen && filteredItems.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded shadow max-h-60">
                {filteredItems.map((item) => (
                  <li
                    key={item.ItemId}
                    onClick={() => handleItemSelect(item)}
                    className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                  >
                    <strong>
                      {item.ItemNo || item.ItemCode || "N/A"}
                    </strong>{" "}
                    / {item.ItemName || "Unnamed"} - Rs.
                    {item.Rate ?? item.SaleRate ?? 0}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label
              htmlFor="quantity"
              className="block text-sm font-medium text-gray-700"
            >
              Quantity*
            </label>
            <input
              type="number"
              id="quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
            />
          </div>

          {/* Rate */}
          <div>
            <label
              htmlFor="rate"
              className="block text-sm font-medium text-gray-700"
            >
              Rate*
            </label>
            <input
              type="number"
              id="rate"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
            />
          </div>
        </div>

        {/* Add Button */}
        <div className="mt-2 text-right">
          <Button
            onClick={handleAddItem}
            disabled={loading || cpid > 0 || vrid > 0}
          >
            {loading ? "Adding..." : "Add Item"}
          </Button>
        </div>
      </div>

      {/* Order Items Table */}
      <h3 className="mt-4 mb-2 text-lg font-semibold">Order Items</h3>
      <div className="relative overflow-x-auto">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
            <Spinner />
          </div>
        )}
        <table className="w-full text-sm border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-left">Item</th>
              <th className="px-2 py-2 text-right">Qty</th>
              <th className="px-2 py-2 text-right">Rate</th>
              <th className="px-2 py-2 text-right">Total</th>
              <th className="px-2 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {normalizedItems.map((item, idx) => {
              const id = item.Seqid ?? item.Itemid ?? idx;
              const itemName =
                item.Partno ?? item.ItemNo ?? item.ItemCode ?? "Unnamed";
              const rateNum = Number(item.Rate ?? item.SaleRate ?? 0);
              const qtyNum = Number(item.Qty ?? 0);
              const net = Number(item.__Net ?? 0);

              return (
                <tr key={id} className="border-t">
                  <td className="px-2 py-1">{itemName}</td>
                  <td className="px-2 py-1 text-right">{qtyNum}</td>
                  <td className="px-2 py-1 text-right">{rateNum.toFixed(2)}</td>
                  <td className="px-2 py-1 text-right">{net.toFixed(2)}</td>
                  <td className="px-2 py-1 text-center">
                    <Button
                      onClick={() => handleDeleteItem(item)}
                      disabled={loading}
                      className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </Button>

                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-bold bg-gray-100 border-t">
              <td colSpan="3" className="p-2 text-right">
                Total Amount
              </td>
              <td className="p-2 text-right">{totalAmount.toFixed(2)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default StockItems;
